<script lang="ts">
	import { getContext, setContext, onDestroy, onMount, tick } from 'svelte';
	import type { Writable } from 'svelte/store';
	import { writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { format } from 'svelte-i18n';
	import { Button, ButtonV2, Container, Text } from '@Shelly/shelly-design-system';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import MainStep from 'src/components/Devices/DeviceWizard/AddEditSteps/MainStep.svelte';
	import SelectRoomStep from 'src/components/Devices/DeviceWizard/AddEditSteps/SelectRoomStep.svelte';
	import { devices } from 'src/stores/lists/devices';
	import { performDeviceEditSave } from 'src/helpers/edit_device_save_helper';
	import { capFirstChar } from 'src/helpers/utilities';
	import type { Device } from 'src/types/Device';

	export let device: Device;

	let editDevice: Device = { ...device };
	let requestLoader = false;
	let selectedRef: HTMLElement = null;
	let updateFullHeight;

	$: ButtonComp = $showNewLayout ? ButtonV2 : Button;

	$: if (device && editDevice?.id !== device?.id) {
		editDevice = { ...device };
	}

	const createNewRoomStore = writable({
		newRoom: undefined,
		newRoomEditing: false,
	});

	setContext('newRoomStore', createNewRoomStore);

	const onDetailsSaved =
		getContext<(payload: { roomChanged: boolean; roomId?: number; shared?: boolean }) => void>(
			'onDetailsSaved',
		);

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	const onSave = async () => {
		if (!editDevice?.name || editDevice.name.trim() === '') return;
		const initialRoomId = device.room_id;
		requestLoader = true;
		registerStickySave();
		try {
			const storeDevice = $devices[editDevice.id];
			const toSave: Device = {
				...editDevice,
				name: editDevice.name.trim(),
				name_sync: storeDevice?.name_sync ?? editDevice.name_sync,
				name_sync_target: storeDevice?.name_sync_target ?? editDevice.name_sync_target,
			};
			await performDeviceEditSave(toSave);
			devices.update(toSave);
			editDevice = { ...toSave };
			onDetailsSaved?.({
				roomChanged: toSave.room_id !== initialRoomId,
				roomId: toSave.room_id,
				shared: toSave.shared,
			});
		} finally {
			requestLoader = false;
			registerStickySave();
		}
	};

	$: isSaveDisabled = !editDevice?.name || editDevice.name.trim() === '' || requestLoader;
	$: isAddingRoom = !!$createNewRoomStore?.newRoomEditing;

	const registerStickySave = () => {
		if (!stickyBottomAction) return;
		if ($showNewLayout && !isAddingRoom) {
			stickyBottomAction.set({
				onClick: onSave,
				loading: requestLoader,
				disabled: isSaveDisabled,
				icon: 'fas fa-save',
			});
		} else {
			stickyBottomAction.set(null);
		}
	};

	$: if (stickyBottomAction) {
		$showNewLayout;
		isSaveDisabled;
		requestLoader;
		isAddingRoom;
		registerStickySave();
	}

	onMount(async () => {
		registerStickySave();
		await tick();
		registerStickySave();
	});

	onDestroy(() => {
		stickyBottomAction?.update((current) =>
			current?.onClick === onSave ? null : (current ?? null),
		);
	});
</script>

<MainStep bind:editDevice />

<Container type="borderSolid" backgroundColor="color-base" class="mt-m" noScroll>
	<Text element="h2" class="mb-s text-400">
		{capFirstChar($format('settings_title_location', { default: 'Device Room' }))}
	</Text>
	<SelectRoomStep bind:editDevice bind:selectedRef bind:updateFullHeight {requestLoader} />
</Container>

{#if !$showNewLayout}
	<div class="mt-l">
		<svelte:component
			this={ButtonComp}
			class="width-sm-full"
			variant="primary"
			size="m"
			responsiveSize={{ sm: 'm' }}
			loading={requestLoader}
			disabled={isSaveDisabled}
			icon="fas fa-save"
			on:click={onSave}
			aria-label={$format('button_save', { default: 'Save' })}
		>
			{$format('button_save', { default: 'Save' })}
		</svelte:component>
	</div>
{/if}
